import { ChangeDetectorRef, Component, Input, forwardRef, Inject, OnInit, AfterViewInit } from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormControl,
  NG_VALUE_ACCESSOR,
  UntypedFormGroup,
  NG_VALIDATORS,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { getParsedValue, getStringValue } from '../pql-editor/pql-editor.helper';
import { IValue } from '@platform500services/p500-ui-kit';
import { typeToControlsMap, hoursFromData, hoursToData, daysData, affSubData, targetTags } from './data.constants';
import { DestroySubscribers } from '../../../decorators/destroy-subscribers.decorator';
import { IDataService, IParsedDataResponse } from '../../../services/data/data-service.interface';
import { map, startWith, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';

@Component({
  selector: 'gtd-pql-editor-dynamic',
  templateUrl: './pql-editor-dynamic.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PqlEditorDynamicComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: PqlEditorDynamicComponent,
      multi: true,
    },
  ],
})
@DestroySubscribers()
export class PqlEditorDynamicComponent implements ControlValueAccessor, OnInit, AfterViewInit {
  @Input() conditions;
  @Input() type: string;

  public form = new UntypedFormGroup({
    pql: new UntypedFormControl(null),
    country: new UntypedFormControl(null),
    affiliateUUID: new UntypedFormControl(null),
    offerUUID: new UntypedFormControl(null),
    affSub: new UntypedFormControl(null),
    affSubX: new UntypedFormControl(null),
    dayOfWeek: new UntypedFormControl(null),
    dayOfWeekFrom: new UntypedFormControl(null),
    dayOfWeekTo: new UntypedFormControl(null),
    hourOfDayFrom: new UntypedFormControl(null),
    hourOfDayTo: new UntypedFormControl(null),
    advertiserUUID: new UntypedFormControl(null),
    offerURLUUID: new UntypedFormControl(null),
    affiliateSource: new UntypedFormControl(null),
    os: new UntypedFormControl(null),
    devices: new UntypedFormControl(null),
    deviceBrands: new UntypedFormControl(null),
    browsers: new UntypedFormControl(null),
    regions: new UntypedFormControl(null),
    mobileCarriers: new UntypedFormControl(null),
    goalTypeUUID: new UntypedFormControl(null),
  });
  public daysData = daysData;
  public hoursFromData: { [key: string]: string } = hoursFromData;
  public hoursToData: { [key: string]: string } = hoursToData;
  public affSubData = affSubData;
  public globalData: IParsedDataResponse;
  public countriesData = {};
  public offersUrlsDependentData: Observable<Record<string, string>>;
  public targetTags = targetTags;

  private parsedPql: IValue[];
  private subscribers: any = {};
  protected onChange: any = () => {};
  private onTouch: any = () => {};

  constructor(@Inject('IDataService') private dataService: IDataService, private cd: ChangeDetectorRef) {
    this.globalData = this.dataService.getData();
    Object.keys(this.globalData.countries).forEach((countryCode) => {
      if (countryCode !== '**') {
        this.countriesData[countryCode] = this.globalData.countries[countryCode];
      }
    });
  }

  ngOnInit(): void {
    this.cd.detectChanges();
    const controlNames = typeToControlsMap[this.type] || [];
    for (const name of controlNames) {
      switch (name) {
        case 'dayOfWeek':
          this.setControlRequired('dayOfWeekFrom');
          this.setControlRequired('dayOfWeekTo');
          break;
        default:
          this.setControlRequired(name);
      }
    }
    this.subscribers.formChanges = this.form.valueChanges.subscribe(() => {
      if (this.type === 'free') {
        this.onChange(this.pqlControl.value);
      } else {
        const pqlValue = controlNames.map((name) => this.controlsToPql(name));
        const pqlString = getStringValue(pqlValue);
        this.onChange(pqlString);
      }
      this.onTouch();
    });

    this.subscribers.hourOfDayFrom = this.controlByName('hourOfDayFrom').valueChanges.subscribe(() => {
      const fromValue = this.controlByName('hourOfDayFrom').value;
      const toValue = this.controlByName('hourOfDayTo').value;
      if (fromValue && toValue && +fromValue > +toValue) {
        this.controlByName('hourOfDayTo').setValue(null);
      }
      this.hoursToData = fromValue
        ? Object.values(hoursToData).reduce((acc, value, i) => (i >= fromValue ? { ...acc, [i + 1]: value } : acc), {})
        : { ...hoursToData };
    });
  }

  ngAfterViewInit(): void {
    const offerControl = this.controlByName('offerUUID');
    this.offersUrlsDependentData = offerControl.valueChanges.pipe(
      map((id) => this.globalData.offer_urls_by_offer[id]),
      tap(() => {
        const dependForm = this.controlByName('offerURLUUID');
        dependForm.reset();
        dependForm.markAsTouched();
      }),
      startWith(this.globalData.offer_urls_by_offer[offerControl.value])
    );
  }

  setControlRequired(name: string): void {
    const control = this.controlByName(name);
    if (control) {
      control.setValidators(Validators.required);
      control.updateValueAndValidity();
    }
  }

  controlsToPql(name: string): IValue {
    const control = this.controlByName(name);
    let value = control ? control.value : null;
    let operator = 'IN';
    let key = name;

    if (!Array.isArray(value)) {
      operator = '=';
    }

    if (name === 'hourOfDayFrom') {
      operator = '>=';
      key = 'hourOfDay';
    }
    if (name === 'hourOfDayTo') {
      operator = '<';
      key = 'hourOfDay';
    }
    if (name === 'dayOfWeek') {
      const fromControl = this.controlByName('dayOfWeekFrom');
      const toControl = this.controlByName('dayOfWeekTo');
      const fromValue = fromControl ? fromControl.value : null;
      const toValue = toControl ? toControl.value : null;
      const fromIndex = daysData.indexOf(fromValue);
      const toIndex = daysData.indexOf(toValue);
      if ((fromIndex >= 0 && toIndex < 0) || fromIndex === toIndex) {
        value = daysData.slice(fromIndex, fromIndex + 1);
      }
      if (fromIndex < 0 && toIndex >= 0) {
        value = daysData.slice(toIndex, toIndex + 1);
      }
      if (fromIndex >= 0 && toIndex >= 0) {
        if (fromIndex < toIndex) {
          value = daysData.slice(fromIndex, toIndex + 1);
        }
        if (fromIndex > toIndex) {
          value = daysData.slice(fromIndex).concat(daysData.slice(0, toIndex + 1));
        }
      }
      operator = 'IN';
    }
    if (name === 'affSub') {
      const affSubXControl = this.controlByName('affSubX');
      key = affSubXControl && affSubXControl.value ? affSubXControl.value : Object.keys(affSubData)[0];
      operator = '=';
    }

    return {
      key,
      config: 'AND',
      operator,
      value,
    };
  }

  get pqlControl(): UntypedFormControl {
    return this.form.get('pql') as UntypedFormControl;
  }

  controlByName(name: string): UntypedFormControl {
    return this.form.get(name) as UntypedFormControl;
  }

  writeValue(data: string): void {
    if (!data) {
      return;
    }
    this.parsedPql = getParsedValue(data);

    if (this.type === 'free') {
      this.pqlControl.setValue(data, { emitEvent: false });
    } else {
      const controlNames = typeToControlsMap[this.type] || [];
      controlNames.forEach((name) => this.initControl(name));
    }
    this.cd.detectChanges();
    this.form.updateValueAndValidity();
  }

  initControl(name: string): void {
    let pql = this.parsedPql.find((item) => item.key === name);
    if (name === 'hourOfDayFrom') {
      pql = this.parsedPql.find((item) => item.key === 'hourOfDay' && item.operator === '>=');
    }
    if (name === 'hourOfDayTo') {
      pql = this.parsedPql.find((item) => item.key === 'hourOfDay' && ['<=', '<'].includes(item.operator));
    }
    if (name === 'affSub') {
      let affSubNumber = null;
      pql = this.parsedPql.find((item) => {
        if (Object.keys(affSubData).includes(item.key)) {
          affSubNumber = item.key;
          return true;
        } else {
          return false;
        }
      });
      this.controlByName('affSubX').setValue(affSubNumber, { emitEvent: false });
    }
    const value = pql ? pql.value : null;
    const control = this.controlByName(name);
    if (control) {
      control.setValue(value, { onlySelf: true });
    }

    if (name === 'dayOfWeek') {
      const fromControl = this.controlByName('dayOfWeekFrom');
      const toControl = this.controlByName('dayOfWeekTo');
      if (value?.length) {
        fromControl.setValue(this.getCamelCase(value[0]), { emitEvent: false });
        toControl.setValue(this.getCamelCase(value[value.length - 1]), { emitEvent: false });
      }
    }
  }

  getCamelCase(str: string): string {
    return str[0].toUpperCase() + str.slice(1).toLowerCase();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  validate(): ValidationErrors | null {
    const invalid = this.form.invalid;
    return invalid ? { invalid: true } : null;
  }
}
